import { CONTEST_PHASE, IAdvancedContest, IContest } from '../../types/global/helper';
import moment, { Duration } from 'moment';

export const getCurrentContestPhaseEndData = (contest: IContest) => {
    const contestPhase = contest?.contest_phase_dates?.find(phase => phase.phase === contest.contest_phase);
    return contestPhase;
};
export const checkContestStatus = (contestObject: IAdvancedContest): string => {
    const { contest_phase, contest_phase_dates } = contestObject || {};

    if (!contest_phase_dates || contest_phase_dates.length < 1) {
        return 'closed';
    }

    const cloned = [...contest_phase_dates];
    const sortedDates = cloned.sort((a, b) => moment(a.date_started).diff(moment(b.date_started)));
    const firstItem = sortedDates[0];
    const lastItem = sortedDates[sortedDates.length - 1];
    const today = moment();

    if (moment(firstItem.date_started).isAfter(today)) {
        // If the first item's date_started is in the future
        const timeUntilStart = moment.duration(moment(firstItem.date_started).diff(today));

        const days = timeUntilStart.days();
        const hours = timeUntilStart.hours();
        const minutes = timeUntilStart.minutes();

        if (days > 0) {
            return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''} until ${
                firstItem.phase
            }  begins`;
        } else if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''} until ${
                firstItem.phase
            } begins`;
        } else {
            return `${minutes} minute${minutes > 1 ? 's' : ''} until ${firstItem.phase} begins`;
        }
    }

    if (moment(lastItem.expiration_date).isBefore(today)) {
        return 'closed';
    }

    const matchingPhase = sortedDates.find(item => item.phase === contest_phase);

    if (matchingPhase) {
        const expirationDate = moment(matchingPhase.expiration_date);
        const duration = moment.duration(expirationDate.diff(today));

        const days = duration.days();
        const hours = duration.hours();
        const minutes = duration.minutes();

        if (matchingPhase === lastItem) {
            let timeLeftString = '';

            if (days > 0) {
                timeLeftString = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
            } else if (hours > 0) {
                timeLeftString = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else {
                timeLeftString = `${minutes} minute${minutes > 1 ? 's' : ''}`;
            }

            return `${timeLeftString} until close`;
        } else {
            let timeLeftString = '';

            if (days > 0) {
                timeLeftString = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`;
            } else if (hours > 0) {
                timeLeftString = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else {
                timeLeftString = `${minutes} minute${minutes > 1 ? 's' : ''}`;
            }

            return `${timeLeftString} left (${matchingPhase.phase})`;
        }
    }

    return 'closed';
};

export const getCurrentContestPhase = (contestObject: IContest): string => {
    const { contest_phase_dates } = contestObject || {};

    if (!contest_phase_dates || contest_phase_dates.length === 0) {
        return 'in preparation';
    }

    const cloned = [...contest_phase_dates];
    const sortedDates = cloned.sort((a, b) => moment(a.date_started).diff(moment(b.date_started)));
    const firstItem = sortedDates[0];
    const lastItem = sortedDates[sortedDates.length - 1];
    const today = moment();

    if (moment(firstItem.date_started).isAfter(today)) {
        // If the first item's date_started is in the future
        const timeUntilStart = moment.duration(moment(firstItem.date_started).diff(today));
        return `starting in ${timeUntilStart.humanize()}`;
    }
    if (contestObject.contest_phase === CONTEST_PHASE.SUBMISSION) {
        return `Join Contest`;
    }
    if (contestObject.contest_phase === CONTEST_PHASE.AUDIENCE_VOTE) {
        return `Voting in progress`;
    }
    if (contestObject.contest_phase === CONTEST_PHASE.EVENT) {
        return `Event In Progress`;
    }
    if (moment(lastItem.expiration_date).isBefore(today) || contestObject.contest_phase === CONTEST_PHASE.CLOSED) {
        return 'Contest Closed';
    }

    const matchingPhase = sortedDates.find(item =>
        today.isBetween(item.date_started, item.expiration_date, null, '[]'),
    );

    if (matchingPhase) {
        const timeUntilExpiration = calculateDuration(today, matchingPhase.expiration_date);
        return `ending in ${timeUntilExpiration.humanize()}`;
    }

    return 'no phase';
};

// Function to calculate duration between two moments
const calculateDuration = (start: moment.Moment, end: string): Duration => {
    const endMoment = moment(end);
    return moment.duration(endMoment.diff(start));
};
